import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import AuthForm from '@/components/AuthForm'
import StyleField from '@/components/StyleField'
import Logo from '@/components/Logo'
import ExtensionCallout from '@/components/ExtensionCallout'
import {
  fetchPromptAnalysis,
  type PromptAnalysis,
  type SuggestionCard,
} from '@/lib/api'
import { DEFAULT_INTENT } from '@/lib/intents'

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return debounced
}

export default function StyleEditorPage() {
  const { user, isAuthenticated, signOut } = useAuth()

  const [showAuth, setShowAuth] = useState(false)
  const [style, setStyle] = useState('epic love song')
  // What the user is making — narrows which dimensions the score grades (see lib/intents).
  const [intent, setIntent] = useState(DEFAULT_INTENT)

  const debouncedStyle = useDebounced(style, 400)

  // Free rule-based scorecard — available to anonymous visitors too.
  const { data: analysis = null } = useQuery<PromptAnalysis | null>({
    queryKey: ['promptAnalysis', debouncedStyle, intent],
    queryFn: () => fetchPromptAnalysis(debouncedStyle, intent),
    enabled: debouncedStyle.trim().length > 0,
    placeholderData: keepPreviousData, // keep the card mounted across refetches (no flicker)
  })

  // Close the signup overlay once the visitor is authenticated.
  useEffect(() => {
    if (isAuthenticated) setShowAuth(false)
  }, [isAuthenticated])

  function applySuggestion(card: SuggestionCard, suggestion: string) {
    setStyle((prev) => {
      if (card.original && prev.includes(card.original)) return prev.replace(card.original, suggestion)
      return prev.trim().length ? `${prev.trim()}, ${suggestion}` : suggestion
    })
  }

  return (
    <main className="mx-auto max-w-6xl px-6 py-12">
      <Header
        right={
          isAuthenticated && user ? (
            <div className="flex items-center gap-3 text-sm">
              <Link to="/billing" className="rounded-full bg-gray-800 px-3 py-1 hover:bg-gray-700">
                {user.creditAccount?.balance ?? 0} credits
              </Link>
              {user.admin && (
                <Link to="/admin" className="text-gray-500 hover:text-gray-300">Admin</Link>
              )}
              <button onClick={signOut} className="text-gray-500 hover:text-gray-300">Sign out</button>
            </div>
          ) : (
            <button
              onClick={() => setShowAuth(true)}
              className="btn-primary px-4 py-2 text-sm"
            >
              Sign up free
            </button>
          )
        }
      />

      <div className="mx-auto mt-8 w-full max-w-3xl">
        <StyleField
          value={style}
          onChange={setStyle}
          analysis={analysis}
          onApply={applySuggestion}
          intent={intent}
          onIntentChange={setIntent}
          action={
            <button onClick={() => navigator.clipboard.writeText(style)} className="hover:text-gray-300">
              Copy for Suno
            </button>
          }
        />

        <div className="mt-6">
          <ExtensionCallout />
        </div>
      </div>

      {showAuth && !isAuthenticated && <AuthOverlay onClose={() => setShowAuth(false)} />}
    </main>
  )
}

function Header({ right }: { right?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <Logo size="lg" showTagline />
      {right}
    </div>
  )
}

function AuthOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="relative w-full max-w-sm rounded-2xl border border-gray-800 bg-gray-950 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute right-4 top-4 text-gray-500 hover:text-gray-300" aria-label="Close">
          ✕
        </button>
        <AuthForm />
      </div>
    </div>
  )
}
